import React, { useState, useEffect } from 'react';
import { Users, Activity, Server, Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';

export function SystemStatus() {
  const [monitoringData, setMonitoringData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchMonitoringData = async () => {
    try {
      const response = await fetch('/api/v1/admin/active-monitoring', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch monitoring data: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setMonitoringData(data.data);
        setLastUpdate(new Date());
        setError(null);
      } else {
        throw new Error(data.error || 'Failed to fetch monitoring data');
      }
    } catch (err) {
      console.error('Error fetching monitoring data:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMonitoringData();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchMonitoringData, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const formatMemory = (bytes) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processing':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'completed':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'failed':
        return 'bg-red-100 text-red-700 border-red-300';
      default:
        return 'bg-gray-100 text-card-foreground border-border';
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading system status...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-card rounded-lg shadow-sm border border-border">
          <div className="p-6">
            <div className="flex items-center justify-center text-red-600">
              <AlertTriangle className="w-5 h-5 mr-2" />
              <span>Error loading system status: {error}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { activeUsers, activeJobs, systemHealth, apiStatus } = monitoringData || {};

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="bg-card rounded-lg shadow-sm border border-border">
          <div className="p-6 pb-2 flex flex-row items-center justify-between space-y-0">
            <h3 className="text-sm font-medium">Active Users</h3>
            <Users className="h-4 w-4 text-gray-400" />
          </div>
          <div className="p-6 pt-0">
            <div className="text-2xl font-bold">{activeUsers?.count || 0}</div>
            <p className="text-xs text-muted-foreground">
              In the last {activeUsers?.timeWindow || '15 minutes'}
            </p>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-sm border border-border">
          <div className="p-6 pb-2 flex flex-row items-center justify-between space-y-0">
            <h3 className="text-sm font-medium">Active Jobs</h3>
            <Activity className="h-4 w-4 text-gray-400" />
          </div>
          <div className="p-6 pt-0">
            <div className="text-2xl font-bold">{activeJobs?.total || 0}</div>
            <div className="flex gap-2 mt-2">
              {activeJobs?.byStatus?.processing > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 border border-blue-300">
                  {activeJobs.byStatus.processing} processing
                </span>
              )}
              {activeJobs?.byStatus?.pending > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700 border border-yellow-300">
                  {activeJobs.byStatus.pending} pending
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-sm border border-border">
          <div className="p-6 pb-2 flex flex-row items-center justify-between space-y-0">
            <h3 className="text-sm font-medium">System Uptime</h3>
            <Clock className="h-4 w-4 text-gray-400" />
          </div>
          <div className="p-6 pt-0">
            <div className="text-2xl font-bold">
              {systemHealth ? formatUptime(systemHealth.uptime) : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              Since last restart
            </p>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-sm border border-border">
          <div className="p-6 pb-2 flex flex-row items-center justify-between space-y-0">
            <h3 className="text-sm font-medium">Memory Usage</h3>
            <Server className="h-4 w-4 text-gray-400" />
          </div>
          <div className="p-6 pt-0">
            <div className="text-2xl font-bold">
              {systemHealth ? formatMemory(systemHealth.memoryUsage.heapUsed) : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              of {systemHealth ? formatMemory(systemHealth.memoryUsage.heapTotal) : 'N/A'} heap
            </p>
          </div>
        </div>
      </div>

      {/* Active Users List */}
      <div className="bg-card rounded-lg shadow-sm border border-border">
        <div className="p-6">
          <h3 className="text-lg font-semibold">Active Users</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Users who have submitted jobs in the last {activeUsers?.timeWindow || '15 minutes'}
          </p>
        </div>
        <div className="p-6 pt-0">
          {activeUsers?.users?.length > 0 ? (
            <div className="space-y-2">
              {activeUsers.users.map((user) => (
                <div key={user.auth_user_id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium">
                      {user.email ? user.email[0].toUpperCase() : '?'}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{user.display_name || user.email}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                  </div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border border-border">
                    <CheckCircle2 className="w-3 h-3 mr-1 text-green-600" />
                    Active
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No active users at the moment
            </div>
          )}
        </div>
      </div>

      {/* Active Jobs List */}
      <div className="bg-card rounded-lg shadow-sm border border-border">
        <div className="p-6">
          <h3 className="text-lg font-semibold">Currently Processing Jobs</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Jobs that are currently being processed by the system
          </p>
        </div>
        <div className="p-6 pt-0">
          {activeJobs?.jobs?.length > 0 ? (
            <div className="space-y-2">
              {activeJobs.jobs.map((job) => (
                <div key={job.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">{job.url}</p>
                      <span 
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(job.status)}`}
                      >
                        {job.status}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {job.userEmail || job.userName || 'Unknown user'} • Started {new Date(job.createdAt).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No jobs are currently being processed
            </div>
          )}
        </div>
      </div>

      {/* API Connection Status */}
      {apiStatus && (
        <div className="bg-card rounded-lg shadow-sm border border-border">
          <div className="p-6">
            <h3 className="text-lg font-semibold">API Connections</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Status of external service connections
            </p>
          </div>
          <div className="p-6 pt-0">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {/* MCP Server */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">MCP Server</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    apiStatus.mcpServer?.status === 'connected' 
                      ? 'bg-green-100 text-green-700 border border-green-300' 
                      : 'bg-red-100 text-red-700 border border-red-300'
                  }`}>
                    {apiStatus.mcpServer?.status || 'unknown'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">{apiStatus.mcpServer?.url || 'Not configured'}</p>
                {apiStatus.mcpServer?.error && (
                  <p className="text-xs text-red-600 mt-1">{apiStatus.mcpServer.error}</p>
                )}
              </div>

              {/* Supabase */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Supabase</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    apiStatus.supabase?.status === 'connected' 
                      ? 'bg-green-100 text-green-700 border border-green-300' 
                      : 'bg-red-100 text-red-700 border border-red-300'
                  }`}>
                    {apiStatus.supabase?.status || 'unknown'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">Database connection</p>
                {apiStatus.supabase?.error && (
                  <p className="text-xs text-red-600 mt-1">{apiStatus.supabase.error}</p>
                )}
              </div>

              {/* Stripe */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Stripe</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    apiStatus.stripe?.status === 'connected' 
                      ? 'bg-green-100 text-green-700 border border-green-300' 
                      : 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                  }`}>
                    {apiStatus.stripe?.status || 'unknown'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">Mode: {apiStatus.stripe?.mode || 'unknown'}</p>
                {apiStatus.stripe?.error && (
                  <p className="text-xs text-red-600 mt-1">{apiStatus.stripe.error}</p>
                )}
              </div>

              {/* LLM Providers */}
              {apiStatus.llmProviders && (
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">LLM Providers</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      apiStatus.llmProviders?.current_provider 
                        ? 'bg-green-100 text-green-700 border border-green-300' 
                        : 'bg-red-100 text-red-700 border border-red-300'
                    }`}>
                      {apiStatus.llmProviders?.current_provider || 'none'}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {apiStatus.llmProviders?.providers && Object.entries(apiStatus.llmProviders.providers).map(([provider, status]) => (
                      <div key={provider} className="flex justify-between mt-1">
                        <span>{provider}:</span>
                        <span className={status.status === 'healthy' ? 'text-green-600' : 'text-red-600'}>
                          {status.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Last Update Info */}
      <div className="text-center text-sm text-muted-foreground">
        Last updated: {lastUpdate.toLocaleTimeString()} • Auto-refreshes every 30 seconds
      </div>
    </div>
  );
}